import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "./api";
import { TrendingUp, DollarSign, Trophy, Building2, Users, ArrowRight } from "lucide-react";

const STAGES = [
  { key: "new", label: "New", color: "#93a3b0" },
  { key: "contacted", label: "Contacted", color: "#5eaadd" },
  { key: "qualified", label: "Qualified", color: "#e0b64a" },
  { key: "proposal", label: "Proposal", color: "#ef7d00" },
  { key: "won", label: "Won", color: "#2e6f36" },
  { key: "lost", label: "Lost", color: "#7a3e3e" },
];

const fmt$ = (n) => "$" + (n || 0).toLocaleString();

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.dashboard()
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  if (err) return <div className="text-red-300">Error: {String(err)}</div>;
  if (!data) return <div className="text-white/60 text-sm">Loading…</div>;

  const total = Math.max(1, data.total_leads);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold">Dashboard</h1>
        <p className="text-white/60 mt-1 text-sm">Business development pipeline &amp; performance.</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Users} label="Total leads" value={data.total_leads} sub={`${data.by_stage.new} new`} tone="green" />
        <KPI icon={DollarSign} label="Pipeline value" value={fmt$(data.pipeline_value)} sub="Active deals" tone="orange" />
        <KPI icon={Trophy} label="Won value" value={fmt$(data.won_value)} sub={`${data.close_rate}% close rate`} tone="green" />
        <KPI icon={Building2} label="Active merchants" value={data.total_merchants} sub="Onboarding + Active" tone="mint" />
      </div>

      {/* Pipeline funnel */}
      <div className="rounded-2xl bg-[#0f1512] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--tt-zam-orange)] font-semibold">Pipeline</div>
            <h2 className="font-display text-xl font-bold mt-1">Leads by stage</h2>
          </div>
          <Link to="/admin/leads" data-testid="dashboard-view-leads" className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white transition">
            View all leads <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="space-y-3">
          {STAGES.map((s) => {
            const v = data.by_stage[s.key] || 0;
            const pct = Math.round((v / total) * 100);
            return (
              <div key={s.key} className="flex items-center gap-3">
                <div className="w-24 text-xs text-white/70 shrink-0">{s.label}</div>
                <div className="flex-1 h-8 rounded-lg bg-white/5 overflow-hidden relative">
                  <div className="h-full transition-all" style={{ width: `${Math.max(pct, v > 0 ? 4 : 0)}%`, background: s.color }} />
                  <div className="absolute inset-0 flex items-center px-3 text-xs font-semibold">{v}</div>
                </div>
                <div className="w-14 text-right text-xs text-white/50">{pct}%</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top sources */}
      <div className="rounded-2xl bg-[#0f1512] border border-white/10 p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--tt-zam-orange)] font-semibold">
          <TrendingUp className="h-3.5 w-3.5" /> Top lead sources
        </div>
        <h2 className="font-display text-xl font-bold mt-1 mb-5">Where deals are coming from</h2>
        {data.top_sources.length === 0 ? (
          <div className="text-sm text-white/50 py-6 text-center">
            No leads yet. <Link to="/admin/import" className="text-[var(--tt-zam-orange)] font-semibold underline underline-offset-2">Import some</Link> to get started.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/50">
                <tr>
                  <th className="text-left px-4 py-2.5">Source</th>
                  <th className="text-right px-4 py-2.5">Leads</th>
                  <th className="text-right px-4 py-2.5">Won</th>
                  <th className="text-right px-4 py-2.5">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {data.top_sources.map((s) => (
                  <tr key={s.source} className="border-t border-white/5 hover:bg-white/5 transition">
                    <td className="px-4 py-3 font-medium">{s.source}</td>
                    <td className="px-4 py-3 text-right">{s.count}</td>
                    <td className="px-4 py-3 text-right">{s.won}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                        s.conversion > 20 ? "bg-green-500/10 text-green-400" : s.conversion > 5 ? "bg-orange-500/10 text-orange-400" : "bg-white/5 text-white/50"
                      }`}>{s.conversion}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const KPI = ({ icon: Icon, label, value, sub, tone = "green" }) => {
  const tones = {
    green: "bg-[var(--tt-green)]/15 text-[var(--tt-mint)]",
    orange: "bg-[var(--tt-zam-orange)]/15 text-[var(--tt-zam-orange)]",
    mint: "bg-[var(--tt-mint)]/15 text-[var(--tt-mint)]",
  };
  return (
    <div className="rounded-2xl bg-[#0f1512] border border-white/10 p-5">
      <div className="flex items-center justify-between">
        <div className={`h-10 w-10 rounded-xl grid place-items-center ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-4 text-2xl md:text-3xl font-display font-bold">{value}</div>
      <div className="text-sm text-white mt-1">{label}</div>
      <div className="text-[11px] text-white/50 mt-1">{sub}</div>
    </div>
  );
};

export default AdminDashboard;
